import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { CATALOG_CATEGORY_LABELS, type CatalogCategory } from '../constants/catalog-categories';

const META_BROWSE_CATEGORIES = new Set<string>([
  CATALOG_CATEGORY_LABELS.previouslyViewed,
  CATALOG_CATEGORY_LABELS.yourFavorites,
  'Your favorites', // older stored label before casing update
]);

const VIEWED_CATEGORIES_STORAGE_KEY = 'prairi.viewedBrowseCategories';

function isRealBrowseCategory(category?: string | null): category is CatalogCategory {
  return !!category && !META_BROWSE_CATEGORIES.has(category);
}

function readStoredCategories(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(VIEWED_CATEGORIES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return {};
    const next: Record<string, string> = {};
    for (const [id, category] of Object.entries(parsed)) {
      if (id && typeof category === 'string' && isRealBrowseCategory(category)) {
        next[id] = category;
      }
    }
    return next;
  } catch {
    return {};
  }
}

function writeStoredCategories(categories: Record<string, string>) {
  try {
    sessionStorage.setItem(VIEWED_CATEGORIES_STORAGE_KEY, JSON.stringify(categories));
  } catch {
    // ignore quota / private mode
  }
}

type GetViewedStartupsResponse = {
  success: boolean;
  viewedStartupIds: string[];
  viewedCategories?: Record<string, string>;
};

/**
 * Fetches and caches startupIds this investor has previously viewed
 * (profile open, scroll on profile, or scroll on pitch deck).
 *
 * `viewedIdsOrdered` is newest-first (reverse chronological).
 * `viewedIds` is a Set for O(1) badge checks.
 * `viewedCategories` maps startupId → real catalog row from the click (session + server).
 * `markViewed` optimistically moves a startup to the front without waiting for refetch.
 */
export function useViewedStartups() {
  const { user, firebaseUser } = useAuth();
  const [viewedIdsOrdered, setViewedIdsOrdered] = useState<string[]>([]);
  const [viewedCategories, setViewedCategories] = useState<Record<string, string>>(() =>
    readStoredCategories()
  );
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  const viewedIds = useMemo(() => new Set(viewedIdsOrdered), [viewedIdsOrdered]);

  const mergeCategories = useCallback((incoming: Record<string, string>) => {
    setViewedCategories(prev => {
      const next = { ...prev };
      let changed = false;
      for (const [id, category] of Object.entries(incoming)) {
        if (!id || !isRealBrowseCategory(category)) continue;
        // Keep an existing session click; only fill gaps from the server
        if (!next[id]) {
          next[id] = category;
          changed = true;
        }
      }
      if (changed) writeStoredCategories(next);
      return changed ? next : prev;
    });
  }, []);

  const applyViewedResponse = useCallback(
    (data: GetViewedStartupsResponse) => {
      if (!data.success || !Array.isArray(data.viewedStartupIds)) return;
      setViewedIdsOrdered(data.viewedStartupIds.filter((id): id is string => !!id));
      if (data.viewedCategories && typeof data.viewedCategories === 'object') {
        const fromServer: Record<string, string> = {};
        for (const [id, category] of Object.entries(data.viewedCategories)) {
          if (id && isRealBrowseCategory(category)) {
            fromServer[id] = category;
          }
        }
        mergeCategories(fromServer);
      }
    },
    [mergeCategories]
  );

  useEffect(() => {
    // Only fetch for authenticated investors, and only once per session
    if (!firebaseUser || !user || user.userType !== 'investor') return;
    if (hasFetched.current) return;
    hasFetched.current = true;

    const functions = getFunctions();

    const fetchViewed = async () => {
      setLoading(true);
      try {
        const getViewedStartups = httpsCallable<Record<string, never>, GetViewedStartupsResponse>(
          functions,
          'getViewedStartups'
        );

        const result = await getViewedStartups({});
        applyViewedResponse(result.data);
      } catch (err) {
        console.error('[useViewedStartups] Error fetching viewed startups:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchViewed();
  }, [firebaseUser, user, applyViewedResponse]);

  /**
   * Optimistically mark a startup as viewed (called on card open / profile scroll).
   * Does NOT call Firebase — the tracking hook already persists the interaction.
   * Newest views go to the front of Previously viewed.
   * Optional `category` stores the real browse row for the profile badge.
   */
  const markViewed = useCallback((startupId: string, category?: string | null) => {
    if (!startupId) return;
    setViewedIdsOrdered(prev => {
      if (prev[0] === startupId) return prev;
      return [startupId, ...prev.filter(id => id !== startupId)];
    });
    // Only handleSelectStartup passes category (real row clicks). Scroll calls omit it.
    if (isRealBrowseCategory(category)) {
      setViewedCategories(prev => {
        if (prev[startupId] === category) return prev;
        const next = { ...prev, [startupId]: category };
        writeStoredCategories(next);
        return next;
      });
    }
  }, []);

  const refreshViewed = useCallback(async () => {
    if (!firebaseUser || !user || user.userType !== 'investor') return;

    const functions = getFunctions();
    try {
      const getViewedStartups = httpsCallable<Record<string, never>, GetViewedStartupsResponse>(
        functions,
        'getViewedStartups'
      );

      const result = await getViewedStartups({});
      applyViewedResponse(result.data);
    } catch (err) {
      console.error('[useViewedStartups] Error refreshing viewed startups:', err);
    }
  }, [firebaseUser, user, applyViewedResponse]);

  return {
    viewedIds,
    viewedIdsOrdered,
    viewedCategories,
    markViewed,
    refreshViewed,
    loading,
  };
}
